/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Image Picker Modal Component
 *
 * Displays a gallery of base64-encoded images and allows the user to select one.
 * This modal is opened from the form builder when a field has an `imagePicker` UI component
 * configured in the schema's `uiSchema.ui:components` array.
 *
 * Key Features:
 * - Renders a grid of clickable image thumbnails passed via `IxActiveModal` data
 * - Pre-selects the image that is already stored for the attribute (via `currentValue` in modal data)
 * - Highlights the currently selected image
 * - Closes with the selected base64 image string on confirmation
 * - Dismisses without changes on cancel
 *
 * Related Modules:
 * - FormBuilderComponent (`form-builder.component.ts`) — opens this modal
 * - SchemaDTO (`Schema.ts`) — defines the `UiComponentDTO` shape
 *
 * Implementation Notes:
 * - Uses the Siemens iX modal API (`IxActiveModal`) for lifecycle management
 * - Modal data shape: `{ images: string[], currentValue?: string | null }`
 * - On close: emits the selected image string; on dismiss: no value is emitted
 */

import {Component, inject, OnInit} from '@angular/core';
import {IxActiveModal} from '@siemens/ix-angular';

/** Shape of the data object passed to this modal via `ModalService.open()`. */
export interface ImagePickerModalData {
  /** Array of base64-encoded image strings to display in the gallery. */
  images: string[];
  /** The already-selected image value to pre-select when the modal opens (optional). */
  currentValue?: string | null;
}

@Component({
  selector: 'app-image-picker-modal',
  standalone: false,
  templateUrl: './image-picker-modal.component.html',
  styleUrl: './image-picker-modal.component.scss',
})
export class ImagePickerModalComponent implements OnInit {
  readonly activeModal = inject(IxActiveModal);

  /** Images passed in via modal data. */
  images: string[] = [];

  /** Currently highlighted image, or `null` if nothing has been selected yet. */
  selectedImage: string | null = null;

  ngOnInit(): void {
    const data = this.activeModal.data as ImagePickerModalData | undefined;
    this.images = data?.images ?? [];
    if (data?.currentValue && this.images.includes(data.currentValue)) {
      this.selectedImage = data.currentValue;
    }
  }

  /**
   * Marks the provided image as the current selection.
   *
   * @param {string} image - The base64-encoded image string to select.
   */
  onImageClick(image: string): void {
    this.selectedImage = image;
  }

  /**
   * Closes the modal and emits the selected image string to the caller.
   * The button is disabled until an image is selected.
   */
  onConfirm(): void {
    this.activeModal.close(this.selectedImage);
  }

  /**
   * Dismisses the modal without returning a value.
   * Any previously selected image is discarded.
   */
  onCancel(): void {
    this.activeModal.dismiss('cancel');
  }
}
